import { useEffect, useMemo, useState } from "react";
import { deleteGasto, listGastos, saveGasto } from "../../lib/data";
import { formatCLP, formatFecha } from "../../lib/format";
import type { Gasto } from "../../lib/types";
import { EmptyState, Modal, Spinner, StatCard } from "../../components/ui";

const HOY = new Date();
const BLANK = (): Gasto => ({
  id: "", fecha: HOY.toISOString().slice(0, 10), ano: HOY.getFullYear(),
  descripcion: "", categoria: "Materiales", monto: 0, responsable: "", creadoEn: "",
});
const CATEGORIAS = ["Materiales", "Arriendo", "Transporte", "Premiación", "Administración", "Otro"];

export function Gastos() {
  const [ano, setAno] = useState(HOY.getFullYear());
  const [items, setItems] = useState<Gasto[]>([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState<Gasto>(BLANK());
  const [error, setError] = useState("");

  async function reload() {
    setLoading(true);
    try { setItems(await listGastos(ano)); } catch (e) { setError((e as Error).message); } finally { setLoading(false); }
  }
  useEffect(() => { reload(); /* eslint-disable-next-line */ }, [ano]);

  const total = useMemo(() => items.reduce((s, g) => s + g.monto, 0), [items]);

  async function guardar() {
    if (!draft.descripcion.trim()) return setError("Descripción obligatoria");
    if (draft.monto <= 0) return setError("Monto debe ser mayor a 0");
    try {
      await saveGasto({ ...draft, ano: new Date(draft.fecha).getFullYear() });
      setOpen(false); reload();
    } catch (e) { setError((e as Error).message); }
  }

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-extrabold text-slate-800">Gastos · caja chica</h1>
          <p className="text-sm text-slate-500">Salidas de dinero de la academia</p>
        </div>
        <div className="flex gap-2">
          <select className="input max-w-[6rem]" value={ano} onChange={(e) => setAno(Number(e.target.value))}>
            {[HOY.getFullYear() - 1, HOY.getFullYear(), HOY.getFullYear() + 1].map((y) => <option key={y} value={y}>{y}</option>)}
          </select>
          <button className="btn-primary" onClick={() => { setDraft(BLANK()); setError(""); setOpen(true); }}>+ Nuevo gasto</button>
        </div>
      </div>

      <div className="grid grid-cols-2 gap-4">
        <StatCard label={`Gasto total ${ano}`} value={formatCLP(total)} tone="rose" />
        <StatCard label="Registros" value={String(items.length)} tone="slate" />
      </div>

      {loading ? <Spinner /> : items.length === 0 ? (
        <EmptyState title="Sin gastos registrados" hint="Registra compras de materiales, arriendos, etc." />
      ) : (
        <div className="card overflow-x-auto">
          <table className="w-full">
            <thead className="border-b border-slate-100">
              <tr>
                <th className="th">Fecha</th><th className="th">Descripción</th><th className="th">Categoría</th>
                <th className="th">Responsable</th><th className="th">Monto</th><th className="th"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {items.sort((a, b) => b.fecha.localeCompare(a.fecha)).map((g) => (
                <tr key={g.id} className="hover:bg-slate-50">
                  <td className="td">{formatFecha(g.fecha)}</td>
                  <td className="td font-medium">{g.descripcion}</td>
                  <td className="td"><span className="badge bg-slate-100 text-slate-600">{g.categoria}</span></td>
                  <td className="td">{g.responsable || "—"}</td>
                  <td className="td font-semibold text-rose-600">{formatCLP(g.monto)}</td>
                  <td className="td text-right">
                    <button className="btn-danger !px-2 !py-1" onClick={async () => { if (confirm("¿Eliminar gasto?")) { await deleteGasto(g.id); reload(); } }}>Eliminar</button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <Modal open={open} onClose={() => setOpen(false)} title="Nuevo gasto">
        <div className="space-y-3">
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Fecha</label><input className="input" type="date" value={draft.fecha} onChange={(e) => setDraft({ ...draft, fecha: e.target.value })} /></div>
            <div>
              <label className="label">Categoría</label>
              <select className="input" value={draft.categoria} onChange={(e) => setDraft({ ...draft, categoria: e.target.value })}>
                {CATEGORIAS.map((c) => <option key={c} value={c}>{c}</option>)}
              </select>
            </div>
          </div>
          <div><label className="label">Descripción</label><input className="input" value={draft.descripcion} onChange={(e) => setDraft({ ...draft, descripcion: e.target.value })} placeholder="Ej: compra de balones" /></div>
          <div className="grid grid-cols-2 gap-3">
            <div><label className="label">Monto</label><input className="input" type="number" value={draft.monto || ""} onChange={(e) => setDraft({ ...draft, monto: Number(e.target.value) })} /></div>
            <div><label className="label">Responsable</label><input className="input" value={draft.responsable} onChange={(e) => setDraft({ ...draft, responsable: e.target.value })} /></div>
          </div>
          {error && <p className="text-sm text-rose-600">{error}</p>}
          <div className="flex justify-end gap-2 pt-2">
            <button className="btn-ghost" onClick={() => setOpen(false)}>Cancelar</button>
            <button className="btn-primary" onClick={guardar}>Guardar</button>
          </div>
        </div>
      </Modal>
    </div>
  );
}
